/**
 * HoverCardPortal — the ONE body-portaled hover card shell (2026-09-21).
 *
 * WHY IT EXISTS. Two surfaces already needed a dark, portaled, placed hover
 * card (the reaction chips; a retired mention row) and a third is coming (the
 * bell's three renderers), and hand-rolling `createPortal` + placement + a
 * ladder rung + the click marker a second and third time is precisely what
 * src/styles/layers.ts's seven-incident preamble and check-overlay-layers.mjs
 * exist to prevent.
 *
 * WHY A PORTAL AT ALL, and not a CSS-only absolute tooltip. Read the lesson on
 * ThreadChipTooltip (styles.ts): `overflow-x: auto` on a rail forces computed
 * `overflow-y: auto`, so a tooltip positioned inside a scrolling list is
 * clipped into invisibility BY CONSTRUCTION and users only ever see the native
 * fallback. Both of this component's consumers live inside a scrolling list.
 *
 * FOUR PROPERTIES ARE LOAD-BEARING, each for a defect that reached production:
 *
 *   1. `data-prest0n-portal="true"` — by VALUE. useGlobalChat's outside-click
 *      handler matches `[data-prest0n-portal="true"]`, and
 *      ThreadArchiveNoticeOverlay once shipped `=""`, which that selector never
 *      matched — so its own scrim closed the chat on every click.
 *   2. The rung comes from the ladder and is passed IN. Which rung depends on
 *      what the card belongs to: the chat's own surfaces are
 *      BODY_PORTAL_Z_INDEX, app chrome that may annotate a dialog is
 *      APP_TOOLTIP_Z_INDEX. The gate forbids restating a ladder number anywhere,
 *      comments included, so this file names neither.
 *   3. `pointerEvents: 'none'` — a readout must never swallow the click meant
 *      for the control underneath it.
 *   4. Hidden until placed. The first paint has no measurement, so an unplaced
 *      card would flash at the viewport origin.
 *
 * `bounds` is not optional decoration: placeReactionPicker clamps into that
 * rect, and omitting it is how a card straddles the docked pane's edge — the
 * 2026-09-09 defect that function exists for.
 */
import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import {
  placeReactionPicker,
  type PickerAnchor,
  type PickerRect,
} from '../../services/messageReactions/pickerPlacement';

/**
 * The dark readout surface both consumers share. Lifted verbatim from
 * ReactionDetails' module-private `HoverCard` so the two cannot drift into two
 * different popovers.
 */
export const DarkHoverCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  max-width: min(300px, calc(100vw - 24px));
  padding: 6px 10px;
  border-radius: 10px;
  background: rgba(20, 20, 20, 0.97);
  border: 1px solid rgba(198, 167, 94, 0.28);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.4);
  color: rgba(255, 255, 255, 0.9);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.78rem;
  line-height: 1.3;
`;

export interface HoverCardPortalProps {
  anchor: PickerAnchor;
  /** The pane rect to clamp into. Pass it — see the header. */
  bounds?: PickerRect | null;
  /** The ladder rung, from src/styles/layers.ts. Never a literal. */
  zIndex: number;
  /** The consumer's own test hook, e.g. `{ 'data-mention-closed-card': id }`. */
  markers?: Record<string, string>;
  /**
   * True when the card duplicates content already in the anchor's accessible
   * name — which is the house contract (ThreadSwitcherBar: full content in
   * `aria-label`, the portal for sighted hover/focus) and therefore the default.
   */
  ariaHidden?: boolean;
  /** Re-place when this changes — the consumer's own content identity. */
  placeKey?: string;
  children: React.ReactNode;
}

export function HoverCardPortal({
  anchor,
  bounds,
  zIndex,
  markers,
  ariaHidden = true,
  placeKey,
  children,
}: HoverCardPortalProps): React.ReactElement | null {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [placement, setPlacement] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const next = placeReactionPicker(
      anchor,
      { width: rect.width || 180, height: rect.height || 30 },
      { width: window.innerWidth, height: window.innerHeight },
      bounds ?? null,
    );
    setPlacement((prev) => (prev && prev.top === next.top && prev.left === next.left
      ? prev
      : { top: next.top, left: next.left }));
  }, [anchor, bounds, placeKey]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <DarkHoverCard
      ref={cardRef}
      role="tooltip"
      aria-hidden={ariaHidden ? 'true' : undefined}
      data-prest0n-portal="true"
      {...(markers || {})}
      style={{
        position: 'fixed',
        top: placement?.top ?? -9999,
        left: placement?.left ?? -9999,
        zIndex,
        pointerEvents: 'none',
        visibility: placement ? 'visible' : 'hidden',
      }}
    >
      {children}
    </DarkHoverCard>,
    document.body,
  );
}

export default HoverCardPortal;
